import { useState, useEffect } from 'react';
import { useStableCallback } from '@olxpwa-lib/utils';
import { Step } from './step';
import type { AssistantStep } from './types';
import { Wrapper } from './styled-components';

type Props = {
  steps: AssistantStep[];
  timeout?: number;
  onEnter?: () => void;
  onExit?: () => void;
  __disable_timeout__?: boolean;
};

export const Assistant = ({
  steps,
  timeout = 5_000,
  __disable_timeout__,
  onEnter: MAYBE_UNSTABLE_onEnter,
  onExit: MAYBE_UNSTABLE_onExit,
}: Props) => {
  const [index, setIndex] = useState(steps.findIndex(({ completed }) => !completed));
  const countableSteps = steps.filter(({ countable }) => countable);
  const completedCountableSteps = countableSteps.filter(({ completed }) => completed).length + 1;
  const nextIndex = steps.findIndex(({ completed }) => !completed);
  const onEnter = useStableCallback(() => MAYBE_UNSTABLE_onEnter?.());
  const onExit = useStableCallback(() => MAYBE_UNSTABLE_onExit?.());
  const step = steps[index] as AssistantStep | undefined;
  const stepExists = !!step;
  const stepTimeout = step?.timeout;

  useEffect(() => {
    if (stepTimeout === 0 || (nextIndex >= 0 && nextIndex < index)) {
      setIndex(nextIndex);

      return;
    }

    if (index === -1) {
      if (nextIndex > index) {
        setIndex(nextIndex);
      }

      return;
    }

    const timeoutHandle = setTimeout(
      () => {
        setIndex(nextIndex);
      },
      __disable_timeout__ ? 0 : stepTimeout ?? timeout,
    );

    return () => {
      clearTimeout(timeoutHandle);
    };
  }, [index, nextIndex, timeout, stepTimeout, onEnter, onExit, __disable_timeout__]);

  useEffect(() => {
    if (stepExists) {
      onEnter?.();

      return () => {
        onExit?.();
      };
    }
  }, [stepExists, onEnter, onExit]);

  if (!step) {
    return null;
  }

  return (
    <Wrapper>
      <Step {...step} stepNumber={completedCountableSteps} totalCount={countableSteps.length} />
    </Wrapper>
  );
};
